import type { RoleAssignment } from "@/api/access";
import { formatDateTime } from "@/lib/format";
import {
  ROLE_LABEL,
  UNKNOWN_ENTRA_USER,
  identityName,
  isRole,
  type SubjectIdentity,
} from "@/lib/editorAccess";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card } from "@/ui/Card";
import { Dialog, DialogContent } from "@/ui/Dialog";

function roleLabel(role: string): string {
  return isRole(role) ? ROLE_LABEL[role] : role;
}

/** A person or actor, with the identifier secondary and selectable. */
function Who({ identity }: { identity: SubjectIdentity }) {
  if (identity.system) {
    return (
      <span className="block">
        <span className="text-sm text-foreground">System job</span>
        <span className="block select-all break-all font-mono text-xs text-muted-foreground">
          {identity.subject}
        </span>
      </span>
    );
  }
  return (
    <span className="block">
      <span className="text-sm text-foreground">
        {identity.displayName ?? UNKNOWN_ENTRA_USER}
        {identity.self ? " (you)" : ""}
      </span>
      <span className="block select-all break-all font-mono text-xs text-muted-foreground">
        {identity.subject}
      </span>
    </span>
  );
}

const HISTORY_COLUMNS = ["Person", "Role", "Granted", "Revoked"] as const;

/** The wide-layout tracks, container-gated like the access table's. */
const COLUMNS =
  "@3xl:grid @3xl:grid-cols-[minmax(0,1.6fr)_minmax(9rem,1fr)_minmax(0,1.4fr)_minmax(0,1.4fr)] @3xl:items-start @3xl:gap-4";

const NARROW_LABEL =
  "block text-xs font-semibold uppercase tracking-wide text-muted-foreground @3xl:hidden";

/**
 * Every revoked grant in this environment, newest revocation first — the
 * retained audit record, never deleted, rendered as inactive history.
 *
 * Same grid-with-table-roles shape as the access table, for the same reason:
 * the semantics survive the stacked narrow layout, and there is one copy of
 * each row for assistive technology.
 */
export function AccessHistorySection({
  revoked,
  identityOf,
}: {
  revoked: readonly RoleAssignment[];
  identityOf: (subject: string) => SubjectIdentity;
}) {
  return (
    <section aria-labelledby="access-history-heading" className="space-y-3">
      <div>
        <h2
          id="access-history-heading"
          className="text-base font-semibold text-foreground"
        >
          Access history
        </h2>
        <p className="text-sm text-muted-foreground">
          Revoked assignments in this environment. They confer nothing and are
          kept so an incident review can answer who granted a role and who took
          it away.
        </p>
      </div>
      {revoked.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No revoked assignments yet.
        </p>
      ) : (
        <Card className="overflow-hidden">
          <div
            role="table"
            aria-label="Revoked Clinical Editor role assignments"
            className="@container"
          >
            <div role="rowgroup">
              <div
                role="row"
                className={`${COLUMNS} border-b border-border px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground @max-3xl:sr-only`}
              >
                {HISTORY_COLUMNS.map((column) => (
                  <span key={column} role="columnheader">
                    {column}
                  </span>
                ))}
              </div>
            </div>
            <div role="rowgroup">
              {revoked.map((item) => (
                <div
                  key={item.assignmentId}
                  role="row"
                  data-assignment={item.assignmentId}
                  className={`${COLUMNS} border-b border-border px-4 py-3 last:border-b-0 @max-3xl:space-y-3`}
                >
                  <div role="cell" className="min-w-0">
                    <span aria-hidden="true" className={NARROW_LABEL}>
                      Person
                    </span>
                    <Who identity={identityOf(item.subject)} />
                  </div>
                  <div role="cell" className="min-w-0">
                    <span aria-hidden="true" className={NARROW_LABEL}>
                      Role
                    </span>
                    <Badge tone="outline">{roleLabel(item.role)}</Badge>
                    <span className="mt-1 block select-all break-all font-mono text-xs text-muted-foreground">
                      {item.assignmentId}
                    </span>
                  </div>
                  <div role="cell" className="min-w-0">
                    <span aria-hidden="true" className={NARROW_LABEL}>
                      Granted
                    </span>
                    <span className="block text-sm text-foreground">
                      {formatDateTime(item.grantedAt)}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      by
                    </span>
                    <Who identity={identityOf(item.grantedBy)} />
                  </div>
                  <div role="cell" className="min-w-0">
                    <span aria-hidden="true" className={NARROW_LABEL}>
                      Revoked
                    </span>
                    <span className="block text-sm text-foreground">
                      {formatDateTime(item.revokedAt)}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      by
                    </span>
                    {item.revokedBy ? (
                      <Who identity={identityOf(item.revokedBy)} />
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </section>
  );
}

/**
 * One person's whole record: what they hold now and everything revoked.
 * Reached from the row's "View access history" action.
 */
export function SubjectHistoryDialog({
  identity,
  live,
  revoked,
  identityOf,
  onClose,
}: {
  identity: SubjectIdentity;
  live: readonly RoleAssignment[];
  revoked: readonly RoleAssignment[];
  identityOf: (subject: string) => SubjectIdentity;
  onClose: () => void;
}) {
  const name = identityName(identity);
  const entries = [
    ...live.map((item) => ({ item, active: true })),
    ...[...revoked]
      .sort((a, b) => (b.revokedAt ?? "").localeCompare(a.revokedAt ?? ""))
      .map((item) => ({ item, active: false })),
  ];
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={`Access history for ${name}`}
        description="Every Clinical Editor role this person has held in this environment, live and revoked."
        className="w-[min(40rem,calc(100vw-2rem))]"
        footer={
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        }
      >
        <p className="mb-3 text-xs text-muted-foreground">
          Entra Object ID:{" "}
          <span className="select-all font-mono text-foreground">
            {identity.subject}
          </span>
        </p>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No assignments recorded.</p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {entries.map(({ item, active }) => (
              <li key={item.assignmentId} className="space-y-1 px-3 py-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={active ? "neutral" : "outline"}>
                    {roleLabel(item.role)}
                  </Badge>
                  <span className="text-xs font-medium text-foreground">
                    {active ? "Active" : "Revoked"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Granted {formatDateTime(item.grantedAt)} by{" "}
                  {identityName(identityOf(item.grantedBy))}
                </p>
                {item.revokedAt ? (
                  <p className="text-xs text-muted-foreground">
                    Revoked {formatDateTime(item.revokedAt)} by{" "}
                    {item.revokedBy ? identityName(identityOf(item.revokedBy)) : "—"}
                  </p>
                ) : null}
                <p className="select-all font-mono text-xs text-muted-foreground">
                  {item.assignmentId}
                </p>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
